import React, { Suspense, lazy, useEffect } from "react";
import {
   BrowserRouter,
   Route,
   Routes,
   useLocation,
   useNavigationType,
} from "react-router-dom";
import "./App.scss";
import Header from "./components/Header/Header";
import Footer from "./components/Footer";
import SEO from "./components/SEO";
import { LanguageProvider } from "./i18n/LanguageContext";
import {
   getClientPerformanceProfile,
   subscribeToPerformanceChanges,
} from "./utils/performance";
import { initPerformanceDebug } from "./utils/performanceDebug";

const Home = lazy(() => import("./pages/Home"));
const Projects = lazy(() => import("./pages/Projects"));
const ServiceDetail = lazy(() => import("./pages/ServiceDetail"));
const ProjectDetail = lazy(() => import("./pages/ProjectDetail"));
const DigitalMyceliumPlayground = lazy(() =>
   import("./pages/DigitalMyceliumPlayground"),
);
const NotFound = lazy(() => import("./pages/NotFound"));

function ScrollToTop() {
   const location = useLocation();
   const navigationType = useNavigationType();

   useEffect(() => {
      if (navigationType !== "POP") {
         window.scrollTo({ top: 0, left: 0, behavior: "auto" });
      }
   }, [location, navigationType]);

   return null;
}

function usePerformanceModeClass() {
   useEffect(() => {
      if (typeof document === "undefined") return undefined;

      const update = () => {
         document.documentElement.classList.toggle(
            "performance-lite",
            getClientPerformanceProfile().isLowPower,
         );
      };

      update();

      return subscribeToPerformanceChanges(update);
   }, []);
}

function usePerformanceDebug() {
   useEffect(() => {
      return initPerformanceDebug();
   }, []);
}

function AppShell() {
   const location = useLocation();
   const isMyceliumPlayground = location.pathname === "/digital-mycelium-matrix";
   const isProjectsPage = location.pathname === "/projects";
   const hideFooter =
      isMyceliumPlayground || isProjectsPage;

   usePerformanceModeClass();
   usePerformanceDebug();

   return (
       <div className={`App${isMyceliumPlayground ? " App--visual-lab" : ""}${isProjectsPage ? " App--projects" : ""}`}>
          <SEO />
          {!isMyceliumPlayground && <Header />}
         <main className="main">
            <Suspense fallback={null}>
               <Routes>
                  <Route path="/" element={<Home />} />
                  <Route path="/projects" element={<Projects />} />
                  <Route path="/projects/:slug" element={<ProjectDetail />} />
                  <Route path="/services/:slug" element={<ServiceDetail />} />
                  <Route
                     path="/digital-mycelium-matrix"
                     element={<DigitalMyceliumPlayground />}
                  />
                  <Route path="*" element={<NotFound />} />
               </Routes>
            </Suspense>
         </main>
         {!hideFooter && <Footer />}
      </div>
   );
}

function App() {
   return (
      <LanguageProvider>
         <BrowserRouter basename="/">
            <AppShell />
             <ScrollToTop />
          </BrowserRouter>
      </LanguageProvider>
   );
}

export default App;
